import React, { useState, useEffect, useCallback } from 'react';
import {
  Wand2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertTriangle,
  Building,
  Shield,
  FileText,
  Layers,
  Download,
  Loader2,
  Zap,
  ArrowRight,
  Search,
  ExternalLink,
  Sparkles,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { notify } from '@core/notifications/notify';

// ─── Types ────────────────────────────────────────────────────

interface FrameworkWithVersions {
  id: number;
  name: string;
  authority?: string;
  description?: string;
  versions: {
    id: number;
    framework_id: number;
    version: string;
    status: 'active' | 'draft' | 'archived';
    activated_at?: string;
  }[];
}

interface AssignableFramework {
  id: number;
  name: string;
  description?: string;
  activeVersionId: number;
  activeVersionName: string;
}

interface LibraryFramework {
  id: string;
  name: string;
  shortName: string;
  authority: string;
  version: string;
  category: string;
  description: string;
  tags: string[];
  scopeCount: number;
  requirementCount: number;
  deployed: boolean;
  deployedFrameworkId: number | null;
}

interface Company {
  id: number;
  name: string;
  industry?: string;
  is_active?: boolean;
}

interface MappingSummary {
  totalMappings: number;
  templateName?: string;
  nodeCount?: number;
}

interface TemplateForMapping {
  id: number;
  name: string;
  status: string;
  category?: string;
}

interface TemplateNode {
  id: number;
  template_id: number;
  node_type: string;
  parent_id?: number;
  name: string;
  code?: string;
  description?: string;
  order_index?: number;
  generates_compliance_page?: boolean;
  evidence_required?: boolean;
  evidence_type?: string;
}

interface ExistingMapping {
  id: number;
  framework_version_id: number;
  template_node_id: number;
  mapping_type: string;
}

interface AssignmentResult {
  assignmentId: number;
  frameworkId: number;
  frameworkVersionId: number;
  companyId: number;
  pagesGenerated: number;
}

// ─── API helpers ──────────────────────────────────────────────

const getCsrfToken = () =>
  document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

// ─── Step indicator ───────────────────────────────────────────

const WIZARD_STEPS = [
  { number: 1, label: 'Select Framework', icon: Shield },
  { number: 2, label: 'Map Template', icon: Layers },
  { number: 3, label: 'Assign to Company', icon: Building },
  { number: 4, label: 'Complete', icon: CheckCircle2 },
];

function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center justify-center gap-1 px-6 py-4 bg-slate-50 border-b border-slate-200">
      {WIZARD_STEPS.map((step, idx) => {
        const isCompleted = step.number < currentStep;
        const isCurrent = step.number === currentStep;
        const Icon = step.icon;

        return (
          <React.Fragment key={step.number}>
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  isCompleted
                    ? 'bg-emerald-500 text-white'
                    : isCurrent
                      ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                      : 'bg-slate-200 text-slate-400'
                }`}
              >
                {isCompleted ? <CheckCircle2 size={16} /> : <Icon size={14} />}
              </div>
              <span
                className={`text-xs font-medium hidden sm:inline ${
                  isCurrent ? 'text-blue-700' : isCompleted ? 'text-emerald-700' : 'text-slate-400'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < WIZARD_STEPS.length - 1 && (
              <div
                className={`w-8 h-0.5 mx-1 ${
                  step.number < currentStep ? 'bg-emerald-400' : 'bg-slate-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Main Wizard Component ────────────────────────────────────

interface FrameworkAssignmentWizardProps {
  onClose: () => void;
  onComplete?: () => void; // callback to refresh parent data
  // Phase 3: when opened from the builder after Publish & Activate, the framework
  // and version are already known — preselect them so the user lands on company-picking.
  preselectedFrameworkId?: number | null;
  preselectedVersionId?: number | null;
}

export const FrameworkAssignmentWizard: React.FC<FrameworkAssignmentWizardProps> = ({
  onClose,
  onComplete,
  preselectedFrameworkId = null,
  preselectedVersionId = null,
}) => {
  // Wizard state — when framework+version are preselected (coming from the builder
  // after Publish & Activate), both framework selection AND template mapping are
  // already done upstream. Jump straight to step 3: company picker.
  const hasPreselection = preselectedFrameworkId !== null && preselectedVersionId !== null;
  const [step, setStep] = useState(hasPreselection ? 3 : 1);
  const [loading, setLoading] = useState(true);

  // Data
  const [allFrameworks, setAllFrameworks] = useState<FrameworkWithVersions[]>([]);
  const [assignableFrameworks, setAssignableFrameworks] = useState<AssignableFramework[]>([]);
  const [libraryFrameworks, setLibraryFrameworks] = useState<LibraryFramework[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Selections — seed from preselection props if provided
  const [selectedFrameworkId, setSelectedFrameworkId] = useState<number | null>(preselectedFrameworkId);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(preselectedVersionId);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Action states
  const [deploying, setDeploying] = useState<string | null>(null);
  const [activating, setActivating] = useState<number | null>(null);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [newVersionName, setNewVersionName] = useState('1.0');
  const [showCreateVersion, setShowCreateVersion] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<AssignmentResult | null>(null);
  const [mappingSummary, setMappingSummary] = useState<MappingSummary | null>(null);

  // Step 2: Mapping state
  const [templates, setTemplates] = useState<TemplateForMapping[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateNodes, setTemplateNodes] = useState<TemplateNode[]>([]);
  const [existingMappings, setExistingMappings] = useState<ExistingMapping[]>([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<number>>(new Set());
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [mappingProgress, setMappingProgress] = useState({ done: 0, total: 0 });

  // ─── Data loading ─────────────────────────────────────────

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [fwRes, assignableRes, libRes, compRes] = await Promise.all([
        fetch('/api/compliance/definition/framework-versions', { headers: authHeaders() }),
        fetch('/api/compliance/definition/frameworks/assignable', { headers: authHeaders() }),
        fetch('/api/compliance/definition/framework-library', { headers: authHeaders() }).catch(() => null),
        fetch('/api/companies?limit=500&sortBy=createdAt&sortOrder=desc', { headers: authHeaders() }),
      ]);

      if (fwRes.ok) {
        const r = await fwRes.json();
        setAllFrameworks(r.data || []);
      }
      if (assignableRes.ok) {
        const r = await assignableRes.json();
        setAssignableFrameworks(r.data || []);
      }
      if (libRes && libRes.ok) {
        const r = await libRes.json();
        setLibraryFrameworks(r.data || []);
      }
      if (compRes.ok) {
        const r = await compRes.json();
        setCompanies(r.data || []);
      }
    } catch {
      notify.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // ─── Derived state ────────────────────────────────────────

  const selectedFramework = allFrameworks.find((f) => f.id === selectedFrameworkId);
  const activeVersion = selectedFramework?.versions.find((v) => v.status === 'active');
  const draftVersions = selectedFramework?.versions.filter((v) => v.status === 'draft') || [];
  const isAssignable = assignableFrameworks.some((f) => f.id === selectedFrameworkId);
  const assignableInfo = assignableFrameworks.find((f) => f.id === selectedFrameworkId);
  const undeployedLibrary = libraryFrameworks.filter((f) => !f.deployed);

  const filteredFrameworks = allFrameworks.filter(
    (f) =>
      !searchTerm ||
      f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.authority?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ─── Actions ──────────────────────────────────────────────

  const handleDeployFromLibrary = async (libFw: LibraryFramework) => {
    setDeploying(libFw.id);
    try {
      const res = await fetch('/api/compliance/definition/framework-library/deploy', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ libraryId: libFw.id }),
      });
      if (!res.ok) throw new Error('Deploy failed');
      const json = await res.json();
      notify.success(`Deployed ${libFw.shortName}. A draft template was created — publish it in Step 2 before assignment.`);

      // Reload data and auto-select the deployed framework
      await loadAllData();
      if (json.data?.frameworkId) {
        setSelectedFrameworkId(json.data.frameworkId);
      }
    } catch {
      notify.error(`Failed to deploy ${libFw.shortName}`);
    } finally {
      setDeploying(null);
    }
  };

  const handleActivateVersion = async (versionId: number) => {
    setActivating(versionId);
    try {
      const res = await fetch(
        `/api/compliance/definition/framework-versions/versions/${versionId}/activate`,
        { method: 'POST', headers: authHeaders() }
      );
      if (!res.ok) throw new Error('Activation failed');
      notify.success('Version activated');
      await loadAllData();
    } catch {
      notify.error('Failed to activate version');
    } finally {
      setActivating(null);
    }
  };

  const handleCreateVersion = async (activate: boolean) => {
    if (!selectedFrameworkId || !newVersionName.trim()) return;

    setCreatingVersion(true);
    try {
      const res = await fetch('/api/compliance/definition/framework-versions/versions', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          framework_id: selectedFrameworkId,
          version: newVersionName.trim(),
          status: 'draft',
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to create version');
      }
      const json = await res.json();
      const createdVersionId = json.data?.id;

      if (activate && createdVersionId) {
        const activateRes = await fetch(
          `/api/compliance/definition/framework-versions/versions/${createdVersionId}/activate`,
          { method: 'POST', headers: authHeaders() }
        );
        if (!activateRes.ok) throw new Error('Created version but failed to activate');
        notify.success(`Version ${newVersionName.trim()} created and activated`);
      } else {
        notify.success(`Version ${newVersionName.trim()} created as draft`);
      }

      setShowCreateVersion(false);
      setNewVersionName('1.0');
      await loadAllData();
    } catch (err: any) {
      notify.error(err.message || 'Failed to create version');
    } finally {
      setCreatingVersion(false);
    }
  };

  // ─── Step 2: Template & mapping helpers ────────────────────

  const loadTemplates = async () => {
    try {
      const res = await fetch('/api/compliance/definition/templates', {
        headers: authHeaders(),
      });
      if (res.ok) {
        const json = await res.json();
        // Show published and draft, exclude archived
        setTemplates((json.data || []).filter((t: any) => t.status !== 'archived'));
      }
    } catch {
      // non-critical
    }
  };

  const handlePublishTemplate = async (templateId: number) => {
    try {
      const res = await fetch(`/api/compliance/definition/templates/${templateId}/publish`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || err.message || 'Failed to publish');
      }
      notify.success('Template published');
      await loadTemplates();
    } catch (err: any) {
      // Show detailed error — publish often fails due to missing evidence fields
      const msg = err.message || 'Failed to publish template';
      if (msg.includes('evidence fields')) {
        notify.error('Publish failed: Requirements that generate compliance pages need at least one evidence field. Add fields in the Template Builder first.');
      } else {
        notify.error(msg);
      }
    }
  };

  const loadTemplateNodes = async (templateId: number) => {
    setLoadingNodes(true);
    try {
      const tpl = templates.find((t) => t.id === templateId);
      const forBuilder = tpl && tpl.status === 'draft' ? '?forBuilder=true' : '';
      const res = await fetch(`/api/compliance/definition/templates/${templateId}/nodes${forBuilder}`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const json = await res.json();
        setTemplateNodes(json.data || []);
      }
    } catch {
      notify.error('Failed to load template nodes');
    } finally {
      setLoadingNodes(false);
    }
  };

  const loadExistingMappings = async () => {
    if (!selectedFrameworkId || !activeVersion) return;
    try {
      const res = await fetch('/api/compliance/definition/framework-mapping/', {
        headers: authHeaders(),
      });
      if (res.ok) {
        const json = await res.json();
        const allMappings = json.data || [];
        const versionMappings: ExistingMapping[] = allMappings.filter(
          (m: any) => m.framework_version_id === activeVersion.id
        );
        setExistingMappings(versionMappings);
        setMappingSummary({
          totalMappings: versionMappings.length,
          nodeCount: versionMappings.length,
        });
      }
    } catch {
      // non-critical
    }
  };

  const handleMapSelectedNodes = async () => {
    if (!selectedFrameworkId || !activeVersion || selectedNodeIds.size === 0) return;

    setMapping(true);
    const nodeIds = Array.from(selectedNodeIds);
    setMappingProgress({ done: 0, total: nodeIds.length });
    let successCount = 0;

    for (const nodeId of nodeIds) {
      // Skip if already mapped
      if (existingMappings.some((m) => m.template_node_id === nodeId)) {
        setMappingProgress((p) => ({ ...p, done: p.done + 1 }));
        successCount++;
        continue;
      }

      try {
        const res = await fetch('/api/compliance/definition/framework-mapping/framework-mappings', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            framework_id: selectedFrameworkId,
            framework_version_id: activeVersion.id,
            template_node_id: nodeId,
            mapping_type: 'direct',
          }),
        });
        if (res.ok) {
          successCount++;
        } else {
          const errData = await res.json().catch(() => ({}));
          const errMsg = errData.error?.message || errData.message || errData.error || '';
          if (errMsg.includes('already mapped') || errMsg.includes('Duplicate')) {
            successCount++; // treat duplicate as success
          }
        }
      } catch {
        // continue with next
      }
      setMappingProgress((p) => ({ ...p, done: p.done + 1 }));
    }

    if (successCount === nodeIds.length) {
      notify.success(`All ${successCount} node(s) mapped successfully`);
    } else if (successCount > 0) {
      notify.success(`Mapped ${successCount} of ${nodeIds.length} node(s). ${nodeIds.length - successCount} failed — they may need to be in a published template.`);
    } else {
      notify.error('Mapping failed. Ensure the template is published and nodes have evidence fields.');
    }
    setSelectedNodeIds(new Set());
    setMapping(false);

    // Reload mappings + assignable status
    await Promise.all([loadExistingMappings(), loadAllData()]);
  };

  const handleAssign = async () => {
    if (!selectedFrameworkId || !selectedCompanyId) return;

    setAssigning(true);
    try {
      const res = await fetch('/api/compliance/definition/framework-assignments', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          frameworkId: selectedFrameworkId,
          companyId: selectedCompanyId,
          notes: notes || null,
          status: 'active',
        }),
      });

      if (!res.ok) {
        const errData = await res.json();
        const msg = errData.message || 'Assignment failed';
        if (msg.includes('already assigned')) {
          notify.error('This framework is already assigned to this company. Choose a different company or framework.');
        } else if (errData.guidance) {
          const g = errData.guidance;
          const stepsList = g.steps
            ? g.steps.map((s: any, i: number) => `${i + 1}. ${s.label}`).join('\n')
            : '';
          notify.error(g.message + (stepsList ? '\n' + stepsList : (g.action ? '\n→ ' + g.action : '')));
        } else {
          notify.error(msg);
        }
        return;
      }

      const json = await res.json();
      setAssignmentResult(json.data);
      notify.success('Framework assigned successfully!');
      setStep(4);
    } catch (err: any) {
      notify.error(err.message || 'Failed to assign framework');
    } finally {
      setAssigning(false);
    }
  };

  // When moving to step 2, load templates + existing mappings
  useEffect(() => {
    if (step === 2 && selectedFrameworkId && activeVersion) {
      loadTemplates();
      loadExistingMappings();
    }
  }, [step, selectedFrameworkId, activeVersion?.id]);

  // When template is selected, load its nodes
  useEffect(() => {
    if (selectedTemplateId) {
      loadTemplateNodes(selectedTemplateId);
      setSelectedNodeIds(new Set());
    }
  }, [selectedTemplateId]);

  // ─── Navigation ───────────────────────────────────────────

  const canProceedStep1 = selectedFrameworkId !== null && activeVersion !== undefined;
  const canProceedStep2 = isAssignable || existingMappings.length > 0;
  const canProceedStep3 = selectedCompanyId !== null;

  const goNext = () => {
    if (step === 1 && canProceedStep1) setStep(2);
    else if (step === 2 && canProceedStep2) setStep(3);
    else if (step === 3 && canProceedStep3) handleAssign();
  };

  const goBack = () => {
    if (step > 1 && step < 4) setStep(step - 1);
  };

  // ─── Loading state ────────────────────────────────────────

  if (loading) {
    return (
      <>
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Wand2 className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">Framework Assignment Wizard</h3>
              <p className="text-blue-200 text-sm mt-1">Loading...</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="text-blue-600 animate-spin" size={32} />
        </div>
      </>
    );
  }

  // ─── Render ───────────────────────────────────────────────

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 rounded-t-2xl">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-xl">
            <Wand2 className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">
              {hasPreselection ? 'Assign to Company' : 'Framework Assignment Wizard'}
            </h3>
            <p className="text-blue-200 text-sm mt-1">
              {hasPreselection
                ? 'Select a company to assign this framework. Compliance pages will be auto-generated.'
                : (
                    step === 1 ? 'Choose or deploy a framework to assign'
                    : step === 2 ? 'Select a template and map nodes to the framework'
                    : step === 3 ? 'Select a company and complete the assignment'
                    : 'Assignment completed successfully'
                  )}
            </p>
          </div>
        </div>
      </div>

      {/* Step indicator — hidden when entering from a preselected context
          (builder → Publish & Activate → Assign to Company, or Frameworks page
          active version row). Mapping is already done at that point; the user
          only needs to pick a company. */}
      {!hasPreselection && <StepIndicator currentStep={step} />}

      {/* Body */}
      <div className="px-8 py-6 max-h-[60vh] overflow-y-auto">
        {/* ─── STEP 1: Select Framework ─────────────────── */}
        {step === 1 && (
          <div className="space-y-5">
            {/* Case: No frameworks at all */}
            {allFrameworks.length === 0 ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="text-amber-600" size={28} />
                </div>
                <h4 className="text-lg font-bold text-slate-800 mb-2">No Frameworks Created Yet</h4>
                <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                  You need a framework before you can assign one. Deploy a pre-built framework from the library to get started quickly.
                </p>

                {undeployedLibrary.length > 0 ? (
                  <div className="space-y-2 max-w-lg mx-auto">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                      Available from Library
                    </p>
                    {undeployedLibrary.map((libFw) => (
                      <div
                        key={libFw.id}
                        className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <Shield size={16} className="text-blue-600" />
                          <div className="text-left">
                            <p className="text-sm font-semibold text-slate-800">{libFw.shortName}</p>
                            <p className="text-xs text-slate-500">{libFw.authority} &middot; v{libFw.version}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeployFromLibrary(libFw)}
                          disabled={deploying !== null}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {deploying === libFw.id ? (
                            <><Loader2 size={12} className="animate-spin" /> Deploying...</>
                          ) : (
                            <><Download size={12} /> Deploy</>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">
                    No pre-built frameworks available. Create one manually from the Frameworks page.
                  </p>
                )}
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search frameworks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                  />
                </div>

                {/* Framework list */}
                <div className="space-y-2">
                  {filteredFrameworks.map((fw) => {
                    const hasActive = fw.versions.some((v) => v.status === 'active');
                    const isSelected = selectedFrameworkId === fw.id;
                    const isAssignableItem = assignableFrameworks.some((a) => a.id === fw.id);
                    const fwDrafts = fw.versions.filter((v) => v.status === 'draft');
                    const showInlineVersion = isSelected && !hasActive;

                    return (
                      <div
                        key={fw.id}
                        className={`rounded-xl border-2 transition-all overflow-hidden ${
                          isSelected
                            ? showInlineVersion
                              ? 'border-amber-400 bg-amber-50/50 ring-2 ring-amber-100'
                              : 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {/* Framework row */}
                        <button
                          onClick={() => {
                            setSelectedFrameworkId(fw.id);
                            const av = fw.versions.find((v) => v.status === 'active');
                            setSelectedVersionId(av?.id || null);
                            setShowCreateVersion(false);
                          }}
                          className="w-full flex items-center gap-4 p-4 text-left"
                        >
                          <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              isAssignableItem
                                ? 'bg-emerald-100'
                                : hasActive
                                  ? 'bg-blue-100'
                                  : 'bg-amber-100'
                            }`}
                          >
                            <Shield
                              size={18}
                              className={
                                isAssignableItem
                                  ? 'text-emerald-600'
                                  : hasActive
                                    ? 'text-blue-600'
                                    : 'text-amber-600'
                              }
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{fw.name}</p>
                            <p className="text-xs text-slate-500">
                              {fw.authority || 'No authority'} &middot;{' '}
                              {fw.versions.length} version{fw.versions.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {isAssignableItem ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
                                <CheckCircle2 size={10} /> Ready
                              </span>
                            ) : hasActive ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-800 border border-blue-200">
                                <Zap size={10} /> Has Version
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-amber-100 text-amber-800 border border-amber-200">
                                <AlertTriangle size={10} /> No Active Version
                              </span>
                            )}
                            {isSelected && (
                              <CheckCircle2 size={18} className={showInlineVersion ? 'text-amber-600' : 'text-blue-600'} />
                            )}
                          </div>
                        </button>

                        {/* Inline version activate / create panel */}
                        {showInlineVersion && (
                          <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 space-y-3">
                            <div className="flex items-center gap-2">
                              <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
                              <p className="text-xs text-amber-800 font-medium">
                                Activate or create a version to continue.
                              </p>
                            </div>

                            {/* Existing draft versions — activate */}
                            {fwDrafts.length > 0 && (
                              <div className="space-y-1.5">
                                {fwDrafts.map((v) => (
                                  <div
                                    key={v.id}
                                    className="flex items-center justify-between bg-white border border-amber-200 rounded-lg p-2.5"
                                  >
                                    <span className="text-sm text-slate-700 font-medium">
                                      Version {v.version} <span className="text-xs text-slate-400 font-normal">(draft)</span>
                                    </span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleActivateVersion(v.id); }}
                                      disabled={activating !== null || creatingVersion}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                      {activating === v.id ? (
                                        <><Loader2 size={12} className="animate-spin" /> Activating...</>
                                      ) : (
                                        <><Zap size={12} /> Activate</>
                                      )}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Divider */}
                            {fwDrafts.length > 0 && (
                              <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-amber-200" />
                                <span className="text-[10px] font-bold text-amber-400 uppercase">or</span>
                                <div className="flex-1 h-px bg-amber-200" />
                              </div>
                            )}

                            {/* Create new version */}
                            {!showCreateVersion ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowCreateVersion(true);
                                  const existing = fw.versions.map((v) => v.version);
                                  if (existing.length === 0) {
                                    setNewVersionName('1.0');
                                  } else {
                                    const nums = existing.map((v) => parseFloat(v) || 0);
                                    const next = (Math.max(...nums) + 1).toFixed(1);
                                    setNewVersionName(next);
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 rounded-lg transition-colors"
                              >
                                <Plus size={12} /> Create New Version
                              </button>
                            ) : (
                              <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">
                                    Version Number
                                  </label>
                                  <input
                                    type="text"
                                    value={newVersionName}
                                    onChange={(e) => setNewVersionName(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="e.g. 1.0"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                                    disabled={creatingVersion}
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleCreateVersion(true); }}
                                    disabled={creatingVersion || !newVersionName.trim()}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 flex-1"
                                  >
                                    {creatingVersion ? (
                                      <><Loader2 size={12} className="animate-spin" /> Creating...</>
                                    ) : (
                                      <><Zap size={12} /> Create & Activate</>
                                    )}
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleCreateVersion(false); }}
                                    disabled={creatingVersion || !newVersionName.trim()}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-colors disabled:opacity-50"
                                  >
                                    <Plus size={12} /> Create as Draft
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setShowCreateVersion(false); }}
                                    disabled={creatingVersion}
                                    className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Deploy from library section */}
                {undeployedLibrary.length > 0 && (
                  <div className="border-t border-slate-200 pt-4">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Sparkles size={12} /> Or deploy from Framework Library
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {undeployedLibrary.slice(0, 4).map((libFw) => (
                        <button
                          key={libFw.id}
                          onClick={() => handleDeployFromLibrary(libFw)}
                          disabled={deploying !== null}
                          className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all text-left disabled:opacity-50"
                        >
                          <Download size={14} className="text-blue-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-800 truncate">
                              {deploying === libFw.id ? 'Deploying...' : libFw.shortName}
                            </p>
                            <p className="text-[10px] text-slate-500">{libFw.requirementCount} requirements</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── STEP 2: Map Template ──────────────────────── */}
        {step === 2 && (
          <div className="space-y-5">
            {/* Selected framework summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div className="flex items-center gap-3">
                <Shield size={16} className="text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{selectedFramework?.name}</p>
                  <p className="text-xs text-slate-500">
                    Version {activeVersion?.version} &middot; Active
                  </p>
                </div>
                {existingMappings.length > 0 && (
                  <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
                    <CheckCircle2 size={10} /> {existingMappings.length} mapping{existingMappings.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Already assignable — show success + option to add more */}
            {isAssignable && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-emerald-800">Mappings configured — ready to assign</p>
                    <p className="text-xs text-emerald-700 mt-1">
                      You can continue to the next step, or add more mappings below.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No templates at all */}
            {templates.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
                <AlertTriangle size={24} className="text-amber-600 mx-auto mb-3" />
                <p className="text-sm font-bold text-amber-800 mb-1">No Templates Found</p>
                <p className="text-xs text-amber-700 mb-3">
                  You need a template with compliance nodes before you can create mappings.
                </p>
                <a
                  href="/compliance/definition/builder"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 hover:text-blue-900"
                  onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/compliance/definition/builder'; }}
                >
                  <FileText size={12} /> Go to Template Builder <ExternalLink size={10} />
                </a>
              </div>
            ) : (
              <>
                {/* Template selector */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Select Template
                  </label>
                  <div className="space-y-1.5">
                    {templates.map((tpl) => {
                      const isPublished = tpl.status === 'published';
                      const isDraft = tpl.status === 'draft';
                      const isSelected = selectedTemplateId === tpl.id;

                      return (
                        <div
                          key={tpl.id}
                          className={`rounded-xl border-2 transition-all overflow-hidden ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <button
                            onClick={() => setSelectedTemplateId(tpl.id)}
                            className="w-full flex items-center gap-3 p-3 text-left"
                          >
                            <FileText
                              size={16}
                              className={isSelected ? 'text-blue-600' : 'text-slate-400'}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{tpl.name}</p>
                              {tpl.category && <p className="text-xs text-slate-500">{tpl.category}</p>}
                            </div>
                            {isPublished ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                <CheckCircle2 size={10} /> Published
                              </span>
                            ) : isDraft ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                <AlertTriangle size={10} /> Draft
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                                {tpl.status}
                              </span>
                            )}
                            {isSelected && (
                              <CheckCircle2 size={16} className="text-blue-600 flex-shrink-0" />
                            )}
                          </button>

                          {/* Draft template: publish prompt */}
                          {isSelected && isDraft && (
                            <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between">
                              <div className="text-xs text-amber-800">
                                <p>This template must be published before its nodes can be mapped.</p>
                                <p className="mt-1 text-amber-600">Tip: Requirements that generate compliance pages need at least one evidence field. If publish fails, add fields in the Template Builder first.</p>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePublishTemplate(tpl.id); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex-shrink-0 ml-3"
                              >
                                <CheckCircle2 size={12} /> Publish Now
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Template nodes */}
                {selectedTemplateId && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Template Nodes
                      </label>
                      {templateNodes.length > 0 && !mapping && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const unmapped = templateNodes
                                .filter((n) => !existingMappings.some((m) => m.template_node_id === n.id))
                                .map((n) => n.id);
                              setSelectedNodeIds(new Set(unmapped));
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Select all unmapped
                          </button>
                          {selectedNodeIds.size > 0 && (
                            <button
                              onClick={() => setSelectedNodeIds(new Set())}
                              className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {loadingNodes ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="text-blue-600 animate-spin" size={20} />
                        <span className="text-sm text-slate-500 ml-2">Loading nodes...</span>
                      </div>
                    ) : templateNodes.length === 0 ? (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
                        <p className="text-sm text-slate-500">No nodes found in this template.</p>
                      </div>
                    ) : (
                      <>
                        <div className="max-h-52 overflow-y-auto space-y-1 rounded-xl border border-slate-200 p-2 bg-white">
                          {templateNodes.map((node) => {
                            const isMapped = existingMappings.some(
                              (m) => m.template_node_id === node.id
                            );
                            const isChecked = selectedNodeIds.has(node.id);

                            return (
                              <label
                                key={node.id}
                                className={`flex items-center gap-3 p-2.5 rounded-lg transition-all cursor-pointer ${
                                  isMapped
                                    ? 'bg-emerald-50 border border-emerald-200'
                                    : isChecked
                                      ? 'bg-blue-50 border border-blue-200'
                                      : 'hover:bg-slate-50 border border-transparent'
                                }`}
                              >
                                {isMapped ? (
                                  <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                                ) : (
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const next = new Set(selectedNodeIds);
                                      if (e.target.checked) next.add(node.id);
                                      else next.delete(node.id);
                                      setSelectedNodeIds(next);
                                    }}
                                    disabled={mapping}
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-800 truncate">
                                    {node.code ? `${node.code} — ` : ''}{node.name}
                                  </p>
                                  <p className="text-[10px] text-slate-400">
                                    {node.node_type}
                                    {node.generates_compliance_page && ' · generates page'}
                                    {node.evidence_required && ` · evidence: ${node.evidence_type || 'required'}`}
                                  </p>
                                </div>
                                {isMapped && (
                                  <span className="text-[10px] font-bold text-emerald-700 flex-shrink-0">Mapped</span>
                                )}
                              </label>
                            );
                          })}
                        </div>

                        {/* Map action */}
                        {selectedNodeIds.size > 0 && (
                          <button
                            onClick={handleMapSelectedNodes}
                            disabled={mapping}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-xl hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
                          >
                            {mapping ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                Mapping {mappingProgress.done}/{mappingProgress.total}...
                              </>
                            ) : (
                              <>
                                <ArrowRight size={16} />
                                Map {selectedNodeIds.size} Node{selectedNodeIds.size !== 1 ? 's' : ''} to Framework
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── STEP 3: Select Company & Assign ──────────── */}
        {step === 3 && (
          <div className="space-y-5">
            {/* Framework summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Shield size={16} className="text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{selectedFramework?.name}</p>
                  <p className="text-xs text-slate-500">
                    Version {assignableInfo?.activeVersionName || activeVersion?.version}
                  </p>
                </div>
              </div>
            </div>

            {/* Company selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Select Company <span className="text-red-500">*</span>
              </label>
              {companies.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <AlertTriangle size={20} className="text-amber-600 mx-auto mb-2" />
                  <p className="text-sm text-amber-800 font-medium">No Companies Available</p>
                  <p className="text-xs text-amber-600 mt-1">
                    Create a company first before assigning a framework.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {companies.length > 6 && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                      <input
                        type="text"
                        placeholder="Search companies..."
                        className="w-full pl-8 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                        onChange={(e) => {
                          // Simple inline filter - no extra state needed
                          const term = e.target.value.toLowerCase();
                          document.querySelectorAll('[data-company-item]').forEach((el) => {
                            const name = el.getAttribute('data-company-name')?.toLowerCase() || '';
                            (el as HTMLElement).style.display = name.includes(term) ? '' : 'none';
                          });
                        }}
                      />
                    </div>
                  )}
                  <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-xl border border-slate-200 p-2 bg-white">
                    {companies.map((company) => (
                      <button
                        key={company.id}
                        data-company-item
                        data-company-name={company.name}
                        onClick={() => setSelectedCompanyId(company.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                          selectedCompanyId === company.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-transparent hover:bg-slate-50'
                        }`}
                      >
                        <Building
                          size={16}
                          className={
                            selectedCompanyId === company.id ? 'text-blue-600' : 'text-slate-400'
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{company.name}</p>
                          {company.industry && (
                            <p className="text-xs text-slate-500">{company.industry}</p>
                          )}
                        </div>
                        {selectedCompanyId === company.id && (
                          <CheckCircle2 size={16} className="text-blue-600 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                rows={2}
                placeholder="Any notes about this assignment..."
              />
            </div>

            {/* Assignment preview */}
            {selectedCompanyId && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Assignment Summary
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Framework</span>
                    <span className="font-medium text-slate-800">{selectedFramework?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Version</span>
                    <span className="font-medium text-slate-800">
                      {assignableInfo?.activeVersionName || activeVersion?.version}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Company</span>
                    <span className="font-medium text-slate-800">
                      {companies.find((c) => c.id === selectedCompanyId)?.name}
                    </span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <p className="text-xs text-slate-500">
                      Compliance pages will be automatically generated for all mapped requirements.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 4: Success ──────────────────────────── */}
        {step === 4 && (
          <div className="text-center py-4">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="text-emerald-600" size={40} />
            </div>
            <h4 className="text-xl font-bold text-slate-900 mb-2">Assignment Complete!</h4>
            <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
              The framework has been successfully assigned. Compliance pages have been auto-generated for the company.
            </p>

            {/* Result stats */}
            <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs text-blue-600 font-medium">Framework</p>
                <p className="text-sm font-bold text-slate-800 mt-1">{selectedFramework?.name}</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <p className="text-xs text-indigo-600 font-medium">Company</p>
                <p className="text-sm font-bold text-slate-800 mt-1">
                  {companies.find((c) => c.id === selectedCompanyId)?.name}
                </p>
              </div>
              {assignmentResult?.pagesGenerated !== undefined && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 col-span-2">
                  <p className="text-xs text-emerald-600 font-medium">Compliance Pages Generated</p>
                  <p className="text-2xl font-bold text-emerald-800 mt-1">
                    {assignmentResult.pagesGenerated}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => {
                  onComplete?.();
                  onClose();
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
              >
                <CheckCircle2 size={16} /> Done
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer navigation (steps 1-3 only) */}
      {step < 4 && (
        <div className="px-8 py-5 bg-slate-50 rounded-b-2xl border-t border-slate-200 flex items-center justify-between">
          <div>
            {/* Back button hidden when preselected — there's no step 1/2 to go back to */}
            {step > 1 && !hasPreselection && (
              <button
                onClick={goBack}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-xl border border-slate-300 hover:bg-slate-50 transition-all"
              >
                <ChevronLeft size={16} /> Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-slate-500 text-sm font-medium hover:text-slate-700 transition-colors"
            >
              Cancel
            </button>
            {step === 3 ? (
              <button
                onClick={goNext}
                disabled={!canProceedStep3 || assigning}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-medium text-sm rounded-xl hover:bg-emerald-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigning ? (
                  <><Loader2 size={16} className="animate-spin" /> Assigning...</>
                ) : (
                  <><CheckCircle2 size={16} /> Assign Framework</>
                )}
              </button>
            ) : (
              <button
                onClick={goNext}
                disabled={
                  (step === 1 && !canProceedStep1) ||
                  (step === 2 && !canProceedStep2)
                }
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-xl hover:bg-blue-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Footer for step 4 (success) */}
      {step === 4 && (
        <div className="px-8 py-4 bg-slate-50 rounded-b-2xl border-t border-slate-200" />
      )}
    </>
  );
};
